import { useAuthStore, type AuthUser } from '@/stores/authStore';

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:5000/v1';

export type VersionInfo = {
  build: string;
  commit: string;
  env: string;
};

export type TokenResponse = {
  accessToken: string;
  accessExpiresAt: string;
  refreshToken: string;
  refreshExpiresAt: string;
};

/** Senha certa numa conta com 2FA: ainda sem tokens, só o desafio do segundo passo (vale 5 min). */
export type Desafio2FA = { requer2FA: true; desafio: string; expiraEm: string };

export const requer2FA = (r: TokenResponse | Desafio2FA): r is Desafio2FA =>
  (r as Desafio2FA).requer2FA === true;

/** Mesmo formato do usuário da sessão: papel/hospitalId do cliente ativo + vínculos. */
export type MeResponse = AuthUser;

export class ApiError extends Error {
  status: number;
  code?: string;
  /**
   * Mensagem que veio de fato no corpo da resposta (`body.message`).
   * Separada de `message`, que degrada para `res.statusText` ("Conflict", "Forbidden") —
   * texto de protocolo que não serve para mostrar ao usuário. Ver lib/errors.ts.
   */
  serverMessage?: string;
  constructor(message: string, status: number, code?: string, serverMessage?: string) {
    super(message);
    this.status = status;
    this.code = code;
    this.serverMessage = serverMessage;
  }
}

async function rawFetch(path: string, init: RequestInit = {}, withAuth = true): Promise<Response> {
  const headers = new Headers(init.headers);
  if (!headers.has('Content-Type') && init.body) headers.set('Content-Type', 'application/json');
  if (withAuth) {
    const token = useAuthStore.getState().accessToken;
    if (token) headers.set('Authorization', `Bearer ${token}`);
  }
  return fetch(`${API_URL}${path}`, { ...init, headers });
}

let refreshing: Promise<boolean> | null = null;
async function ensureRefreshed(): Promise<boolean> {
  refreshing ??= (async () => {
    const rt = useAuthStore.getState().refreshToken;
    if (!rt) return false;
    const res = await rawFetch('/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refreshToken: rt }),
    }, false);
    if (!res.ok) {
      useAuthStore.getState().clear();
      return false;
    }
    const data = (await res.json()) as TokenResponse;
    useAuthStore.getState().setTokens(data);
    return true;
  })().finally(() => { refreshing = null; });
  return refreshing;
}

/**
 * Instante de expiração (ms) lido do claim `exp` do JWT, ou null se o token não for decodificável.
 * Só lê o payload — a validação da assinatura é do servidor.
 */
export function jwtExpiraEm(token: string): number | null {
  const payload = token.split('.')[1];
  if (!payload) return null;
  try {
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const exp = (JSON.parse(json) as { exp?: unknown }).exp;
    return typeof exp === 'number' ? exp * 1000 : null;
  } catch {
    return null;
  }
}

/** Margem para renovar antes do vencimento: cobre relógio adiantado e latência do handshake. */
const MARGEM_RENOVACAO_MS = 60_000;

/**
 * Access token válido para conexões SignalR, renovando antes se estiver para vencer.
 *
 * O apiFetch renova ao receber 401, mas as conexões de hub não passam por ele: o
 * `accessTokenFactory` é chamado a cada (re)conexão e devolvia o token que estivesse na memória.
 * Com access token de 15 min, numa cirurgia ou sessão longa a reconexão automática usava um token
 * vencido e falhava para sempre.
 */
export async function getFreshAccessToken(agora = Date.now()): Promise<string> {
  const token = useAuthStore.getState().accessToken;
  const exp = token ? jwtExpiraEm(token) : null;
  if (!token || (exp !== null && exp - agora < MARGEM_RENOVACAO_MS)) {
    await ensureRefreshed();
  }
  return useAuthStore.getState().accessToken ?? '';
}

export async function apiFetch<T>(path: string, init: RequestInit = {}, withAuth = true): Promise<T> {
  let res = await rawFetch(path, init, withAuth);
  if (res.status === 401 && withAuth && await ensureRefreshed()) {
    res = await rawFetch(path, init, withAuth);
  }
  if (!res.ok) {
    let code: string | undefined;
    let serverMessage: string | undefined;
    try {
      // Boa parte do backend responde com corpo vazio (Results.Forbid/NotFound) ou só
      // { error }. Quando não há corpo, code e serverMessage ficam undefined e o
      // normalizeError de lib/errors.ts cai no fallback por status HTTP.
      const body = await res.json();
      code = body?.error;
      serverMessage = body?.message;
    } catch { /* corpo vazio ou não-JSON */ }
    throw new ApiError(serverMessage ?? res.statusText, res.status, code, serverMessage);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// --- Convenience ---

export async function getVersion(): Promise<VersionInfo | null> {
  try {
    return await apiFetch<VersionInfo>('/version', {}, false);
  } catch {
    return null;
  }
}

export function login(email: string, senha: string) {
  return apiFetch<TokenResponse | Desafio2FA>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, senha }),
  }, false);
}

/** Segundo passo do login: código do app (6 dígitos) ou código de recuperação. */
export function login2FA(desafio: string, codigo: string) {
  return apiFetch<TokenResponse>('/auth/login/2fa', {
    method: 'POST',
    body: JSON.stringify({ desafio, codigo }),
  }, false);
}

// --- Verificação em duas etapas da própria conta ---

export const iniciar2FA = (senha: string) =>
  apiFetch<{ segredo: string; uriOtpAuth: string }>('/me/2fa/iniciar', { method: 'POST', body: JSON.stringify({ senha }) });

/** Liga o 2FA. Devolve tokens novos (a sessão passa a ter MFA) e os códigos de recuperação, uma única vez. */
export const confirmar2FA = (codigo: string, refreshToken: string) =>
  apiFetch<{ tokens: TokenResponse; codigosRecuperacao: string[] }>('/me/2fa/confirmar', {
    method: 'POST',
    body: JSON.stringify({ codigo, refreshToken }),
  });

export const desativar2FA = (senha: string, codigo: string) =>
  apiFetch<void>('/me/2fa/desativar', { method: 'POST', body: JSON.stringify({ senha, codigo }) });

export const regerarCodigosRecuperacao = (codigo: string) =>
  apiFetch<{ codigosRecuperacao: string[] }>('/me/2fa/codigos-recuperacao', { method: 'POST', body: JSON.stringify({ codigo }) });

export function logout(refreshToken: string) {
  return apiFetch<void>('/auth/logout', {
    method: 'POST',
    body: JSON.stringify({ refreshToken }),
  }, false);
}

export async function getMe(): Promise<AuthUser> {
  const me = await apiFetch<MeResponse>('/me');
  // Só os campos da sessão: o store não guarda o que vier a mais no /me.
  return {
    id: me.id,
    email: me.email,
    nome: me.nome,
    papel: me.papel,
    hospitalId: me.hospitalId,
    vinculos: me.vinculos ?? [],
    doisFAAtivo: me.doisFAAtivo ?? false,
    doisFAObrigatorio: me.doisFAObrigatorio ?? false,
    sessaoComMfa: me.sessaoComMfa ?? false,
    deveTrocarSenha: me.deveTrocarSenha ?? false,
  };
}

/**
 * Troca o cliente ativo. O backend revoga o refresh token atual e devolve um par novo, com
 * papel e hospitalId do outro cliente.
 */
export function trocarCliente(hospitalId: string, refreshToken: string) {
  return apiFetch<TokenResponse>('/auth/trocar-cliente', {
    method: 'POST',
    body: JSON.stringify({ hospitalId, refreshToken }),
  });
}

export function updateMe(data: { nome?: string; foto?: string }) {
  return apiFetch<void>('/me', { method: 'PATCH', body: JSON.stringify(data) });
}

export function trocarSenha(senhaAtual: string, novaSenha: string) {
  return apiFetch<void>('/me/senha', {
    method: 'POST',
    body: JSON.stringify({ senhaAtual, novaSenha }),
  });
}

export function recuperarSenha(email: string) {
  return apiFetch<void>('/auth/recuperar-senha', {
    method: 'POST',
    body: JSON.stringify({ email }),
  }, false);
}

export function resetSenha(token: string, novaSenha: string) {
  return apiFetch<void>('/auth/reset-senha', {
    method: 'POST',
    body: JSON.stringify({ token, novaSenha }),
  }, false);
}

export const apiUrl = API_URL;
